import { useState } from 'react';
import { Button } from '@givebutter/primitives/button';
import * as Popover from '@givebutter/primitives/popover';
import { faAlarmClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import { useCrmMembers } from 'components/tasks/utils/useCrmMembers';
import { TaskFormValues } from 'components/tasks/utils/useTaskForm';
import * as Tooltip from 'components/tooltip';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { toNearestFifteenMinuteDateTime } from 'helpers/dates';
import { DateTime } from 'luxon';

const RemindAtFormField = () => {
    const currentUser = useCurrentUser();
    const [isCustomSelected, setIsCustomSelected] = useState(false);
    const { setValue, watch, control } = Form.useGivebutterFormContext<TaskFormValues>();
    const [remind_at, assigned_to] = watch(['remind_at', 'assigned_to']);

    const { data: members } = useCrmMembers();
    const assignee = members.find(m => m.id.toString() === assigned_to?.toString());

    if (assignee?.id !== currentUser.id) {
        // don't display this field if the current user is not the assignee
        return null;
    }

    return (
        <DropdownMenu.Root>
            <Popover.Root open={isCustomSelected} onOpenChange={setIsCustomSelected}>
                <Tooltip.Root open={remind_at ? undefined : false}>
                    <Tooltip.Trigger>
                        <Popover.Anchor asChild>
                            <DropdownMenu.Trigger asChild disabled={!assignee}>
                                <div className="">
                                    {remind_at ? (
                                        <Button
                                            variant="none"
                                            color="primary"
                                            size="sm"
                                            className="p-2 font-medium hover:bg-gray-100"
                                        >
                                            <FontAwesomeIcon icon={faAlarmClock} />
                                            {remind_at.toFormat('LLL d, h:mma')}
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="transparent"
                                            color="neutral"
                                            className="hover:bg-gray-100"
                                            disabled={!assignee}
                                            shape={'square'}
                                            aria-label="Remind"
                                        >
                                            <FontAwesomeIcon
                                                icon={faAlarmClock}
                                                className="text-gray-500"
                                            />
                                        </Button>
                                    )}
                                </div>
                            </DropdownMenu.Trigger>
                        </Popover.Anchor>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content inverse className="!max-w-56">
                            <Tooltip.Arrow inverse />
                            <p>
                                {remind_at &&
                                    `${assignee.first_name} ${
                                        assignee.last_name
                                    } will be sent an email reminder on ${remind_at.toFormat(
                                        'LLL d, h:mma',
                                    )}`}
                            </p>
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip.Root>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content align="center" side="bottom" sideOffset={8}>
                        <DropdownMenu.Item
                            onSelect={() => {
                                setValue('remind_at', null, { shouldDirty: true });
                            }}
                        >
                            None
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            className="flex items-center justify-between gap-4"
                            onSelect={() => {
                                setValue('remind_at', getRoundedInterval('hour'), {
                                    shouldDirty: true,
                                });
                            }}
                        >
                            <span>An hour from now</span>
                            <span className="text-gray-500">
                                ({getRoundedInterval('hour').toFormat('h:mma ZZZZ')})
                            </span>
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            className="flex items-center justify-between gap-4"
                            onSelect={() => {
                                setValue('remind_at', getRoundedInterval('day'), {
                                    shouldDirty: true,
                                });
                            }}
                        >
                            <span>Tomorrow</span>
                            <span className="text-gray-500">
                                ({getRoundedInterval('day').toFormat('ccc, LLL d, h:mma ZZZZ')})
                            </span>
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            className="flex items-center justify-between gap-4"
                            onSelect={() => {
                                setValue('remind_at', getRoundedInterval('week'), {
                                    shouldDirty: true,
                                });
                            }}
                        >
                            <span>1 week from now</span>
                            <span className="text-gray-500">
                                ({getRoundedInterval('week').toFormat('ccc, LLL d, h:mma ZZZZ')})
                            </span>
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            className="flex items-center justify-between gap-4"
                            onSelect={() => {
                                setValue('remind_at', getRoundedInterval('month'), {
                                    shouldDirty: true,
                                });
                            }}
                        >
                            <span>1 month from now</span>
                            <span className="text-gray-500">
                                ({getRoundedInterval('month').toFormat('ccc, LLL d, h:mma ZZZZ')})
                            </span>
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            onSelect={() => {
                                setIsCustomSelected(true);
                            }}
                        >
                            Custom
                        </DropdownMenu.Item>
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
                <Popover.Portal>
                    <Popover.Content align="center" side="bottom" sideOffset={8}>
                        <Form.UseFormDatePicker
                            control={control}
                            isTextInput={false}
                            selectRange={false}
                            name="remind_at"
                            onChange={() => {
                                setIsCustomSelected(false);
                            }}
                        />
                    </Popover.Content>
                </Popover.Portal>
            </Popover.Root>
        </DropdownMenu.Root>
    );
};

type RemindAtInterval = 'hour' | 'day' | 'week' | 'month';
const getRoundedInterval = (interval: RemindAtInterval) => {
    switch (interval) {
        case 'hour':
            return toNearestFifteenMinuteDateTime(DateTime.now().plus({ hour: 1 }));
        case 'day':
            return toNearestFifteenMinuteDateTime(DateTime.now().plus({ day: 1 }));
        case 'week':
            return toNearestFifteenMinuteDateTime(DateTime.now().plus({ week: 1 }));
        case 'month':
            return toNearestFifteenMinuteDateTime(DateTime.now().plus({ month: 1 }));
    }
};

export { RemindAtFormField };
